import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { isCacheValid } from '../cacheUtils';

// Fetch current user's permissions
export const fetchMyPermissions = createAsyncThunk(
  'rbac/fetchMyPermissions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState().rbac;
    if (isCacheValid(state.lastFetched) && state.myPermissions) {
      return { data: state.myPermissions, cached: true };
    }
    try {
      const res = await Axios.get('/api/rbac/me');
      return { data: res.data.data, cached: false };
    } catch (err) {
      // Don't redirect on failure — just return empty permissions
      // This prevents the view.js handleError from firing /signin redirect
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        // Return empty permissions rather than rejecting
        // PrivateRoute will handle auth redirect via localStorage check
        return { data: null, cached: false };
      }
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch permissions');
    }
  }
);

// Fetch full RBAC config (admin/owner only)
export const fetchRBACConfig = createAsyncThunk(
  'rbac/fetchRBACConfig',
  async (_, { rejectWithValue }) => {
    try {
      const res = await Axios.get('/api/rbac/config');
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch RBAC config');
    }
  }
);

// Fetch all roles
export const fetchRoles = createAsyncThunk(
  'rbac/fetchRoles',
  async (_, { rejectWithValue }) => {
    try {
      const res = await Axios.get('/api/rbac/roles');
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch roles');
    }
  }
);

// Create role
export const createRole = createAsyncThunk(
  'rbac/createRole',
  async (roleData, { rejectWithValue }) => {
    try {
      const res = await Axios.post('/api/rbac/roles', roleData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create role');
    }
  }
);

// Update role
export const updateRole = createAsyncThunk(
  'rbac/updateRole',
  async ({ roleKey, data }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/api/rbac/roles/${roleKey}`, data);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update role');
    }
  }
);

// Delete role
export const deleteRole = createAsyncThunk(
  'rbac/deleteRole',
  async (roleKey, { rejectWithValue }) => {
    try {
      await Axios.delete(`/api/rbac/roles/${roleKey}`);
      return roleKey;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete role');
    }
  }
);

// Assign role to user
export const assignRole = createAsyncThunk(
  'rbac/assignRole',
  async ({ user_id, role_key }, { rejectWithValue }) => {
    try {
      const res = await Axios.post('/api/rbac/assign', { user_id, role_key });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to assign role');
    }
  }
);

// Remove role assignment
export const removeRoleAssignment = createAsyncThunk(
  'rbac/removeRoleAssignment',
  async (userId, { rejectWithValue }) => {
    try {
      await Axios.delete(`/api/rbac/assign/${userId}`);
      return userId;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove assignment');
    }
  }
);

// Fetch assignments
export const fetchAssignments = createAsyncThunk(
  'rbac/fetchAssignments',
  async (_, { rejectWithValue }) => {
    try {
      const res = await Axios.get('/api/rbac/assignments');
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch assignments');
    }
  }
);

const rbacSlice = createSlice({
  name: 'rbac',
  initialState: {
    myPermissions: null,
    config: null,
    roles: [],
    assignments: [],
    loading: false,
    configLoading: false,
    mutationLoading: false,
    error: null,
    lastFetched: null
  },
  reducers: {
    invalidateRBACCache: (state) => {
      state.lastFetched = null;
      state.myPermissions = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // My permissions
      .addCase(fetchMyPermissions.pending, (state) => { state.loading = true; })
      .addCase(fetchMyPermissions.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload.cached) {
          state.myPermissions = action.payload.data; // can be null
          if (action.payload.data) {
            state.lastFetched = Date.now();
          }
        }
      })
      .addCase(fetchMyPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Config
      .addCase(fetchRBACConfig.pending, (state) => { state.configLoading = true; })
      .addCase(fetchRBACConfig.fulfilled, (state, action) => {
        state.configLoading = false;
        state.config = action.payload;
      })
      .addCase(fetchRBACConfig.rejected, (state, action) => {
        state.configLoading = false;
        state.error = action.payload;
      })
      // Roles
      .addCase(fetchRoles.fulfilled, (state, action) => { state.roles = action.payload; })
      // Create role
      .addCase(createRole.pending, (state) => { state.mutationLoading = true; })
      .addCase(createRole.fulfilled, (state, action) => {
        state.mutationLoading = false;
        state.roles.push(action.payload);
      })
      .addCase(createRole.rejected, (state, action) => {
        state.mutationLoading = false;
        state.error = action.payload;
      })
      // Update role
      .addCase(updateRole.pending, (state) => { state.mutationLoading = true; })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.mutationLoading = false;
        const idx = state.roles.findIndex(r => r.key === action.payload.key);
        if (idx >= 0) state.roles[idx] = action.payload;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.mutationLoading = false;
        state.error = action.payload;
      })
      // Delete role
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.roles = state.roles.filter(r => r.key !== action.payload);
      })
      // Assignments
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.assignments = action.payload;
      })
      // Assign
      .addCase(assignRole.fulfilled, (state, action) => {
        const existing = state.assignments.findIndex(a => a.user_id === action.payload.user_id);
        if (existing >= 0) {
          state.assignments[existing] = action.payload;
        } else {
          state.assignments.push(action.payload);
        }
      })
      // Remove assignment
      .addCase(removeRoleAssignment.fulfilled, (state, action) => {
        state.assignments = state.assignments.filter(a => a.user_id !== action.payload);
      });
  }
});

export const { invalidateRBACCache } = rbacSlice.actions;
export default rbacSlice.reducer;